import React, { useState, useCallback } from 'react';
import Modal from '../../../../components/Modal';
import './styles.scss';

interface BulkActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Modal title, e.g. "Change Invoice Status". */
  title: string;
  /** Lead-in verb phrase, e.g. "Change the status of" / "Set the recorded date of". */
  verb: string;
  /** Trailing connector after the count, e.g. "to:". */
  connector: string;
  selectedCount: number;
  isAllFiltered: boolean;
  totalFilteredCount: number;
  /** Whether the field has a valid value and the action can be confirmed. */
  canConfirm: boolean;
  /** Performs the action; should throw on failure. The shell shows the error
   *  and keeps the modal open, or closes it on success. */
  onConfirm: () => Promise<void>;
  /** The field control (dropdown, date input, …). */
  children: React.ReactNode;
  /** Optional explanatory note rendered under the field. */
  hint?: React.ReactNode;
}

/**
 * Shared shell for the invoice register's bulk-action modals (change status,
 * change recorded date). Owns the count/“all filtered” messaging, the
 * submitting/error lifecycle, the all-filtered warning, and the footer buttons.
 * Each concrete modal supplies only its field control and the confirm callback.
 */
const BulkActionModal: React.FC<BulkActionModalProps> = ({
  isOpen,
  onClose,
  title,
  verb,
  connector,
  selectedCount,
  isAllFiltered,
  totalFilteredCount,
  canConfirm,
  onConfirm,
  children,
  hint
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear any stale error each time the modal opens.
  React.useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      console.error('Bulk action failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to update invoices');
    } finally {
      setIsSubmitting(false);
    }
  }, [onConfirm, onClose]);

  const displayCount = isAllFiltered ? totalFilteredCount : selectedCount;
  const invoiceText = displayCount === 1 ? 'invoice' : 'invoices';
  const subject = isAllFiltered
    ? `all ${displayCount} filtered ${invoiceText}`
    : `${displayCount} selected ${invoiceText}`;

  const modalFooter = (
    <div className="bulk-action-modal__footer">
      <button
        type="button"
        className="btn-secondary"
        onClick={onClose}
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={handleConfirm}
        disabled={isSubmitting || !canConfirm}
      >
        {isSubmitting ? 'Updating...' : `Update ${displayCount} ${invoiceText}`}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      hasFooter={true}
      footerContent={modalFooter}
    >
      <div className="bulk-action-modal">
        <p className="bulk-action-modal__description">
          {verb} <strong>{subject}</strong> {connector}
        </p>

        <div className="bulk-action-modal__field">
          {children}
        </div>

        {error && (
          <div className="bulk-action-modal__error">
            {error}
          </div>
        )}

        {isAllFiltered && (
          <div className="bulk-action-modal__warning">
            <strong>Note:</strong> This will update all invoices matching your current filters,
            not just those visible on the current page.
          </div>
        )}

        {hint && (
          <div className="bulk-action-modal__hint">
            {hint}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BulkActionModal;
